import React from "react";
import { AxiosError } from "axios";
import { NavigateFunction } from "react-router-dom";
import {
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  TextField,
} from "@mui/material";
import { OrangeTextButton } from "../../../../common/OrangeTextButton";
import {
  PatriotDeliveryChoice,
  patriotDeliveryDisplayText,
} from "../../../../common/PatriotDeliveryChoice";
import { usePatriotIgnoreZonesOnAlertPage } from "../../../../common/PatriotDrawIgnoreZonesGate";
import { markSiteUnsynced } from "../../../../utils/syncStore";
import { universalAPIErrorHandler } from "../../../../utils/universalAPIErrorHandler";
import {
  BulkAlertSettingsResponse,
  BulkIntegration,
  NO_PERMISSION_TEXT,
  PatriotBulkChanges,
  PatriotBulkFilter,
  bulkErrorMessage,
  cleanFilter,
  countAlerts,
  postBulkAlertSettings,
  skipReasonText,
} from "./bulkAlertSettingsApi";

export interface BulkSite {
  id: number;
  name: string;
}

// One entry per integration the bulk endpoint supports. Adding Immix/Dice/
// Sentinel later = a new entry here plus its change editor and filter fields.
export const BULK_ALERT_INTEGRATIONS: {
  id: BulkIntegration;
  label: string;
  filterFields: { key: keyof PatriotBulkFilter; label: string }[];
}[] = [
  {
    id: "patriot",
    label: "Patriot",
    filterFields: [
      { key: "patriot_client_no", label: "Patriot client no" },
      { key: "patriot_server", label: "Patriot server" },
    ],
  },
];

const plural = (count: number, word: string) =>
  `${count} ${word}${count === 1 ? "" : "s"}`;

export const BulkAlertSettingsDialog = ({
  open,
  onClose,
  sites,
  groupName,
  allSitesInGroup,
  navigate,
}: {
  open: boolean;
  onClose: () => void;
  /** Sites the dialog starts with (the selection, or every site in the group). */
  sites: BulkSite[];
  groupName: string;
  /** True when nothing was selected, so `sites` is the whole group. */
  allSitesInGroup: boolean;
  navigate: NavigateFunction;
}) => {
  const ignoreZonesOnAlertPage = usePatriotIgnoreZonesOnAlertPage();
  const [includedIds, setIncludedIds] = React.useState<number[]>(() =>
    sites.map((site) => site.id)
  );
  const [integration, setIntegration] =
    React.useState<BulkIntegration>("patriot");
  const [changes, setChanges] = React.useState<PatriotBulkChanges>({
    patriot_send_video: false,
    draw_ignore_zones: false,
  });
  const [filter, setFilter] = React.useState<PatriotBulkFilter>({});
  const [preview, setPreview] =
    React.useState<BulkAlertSettingsResponse | null>(null);
  const [result, setResult] = React.useState<BulkAlertSettingsResponse | null>(
    null
  );
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  const integrationConfig =
    BULK_ALERT_INTEGRATIONS.find((option) => option.id === integration) ??
    BULK_ALERT_INTEGRATIONS[0];
  const siteName = (id: number) =>
    sites.find((site) => site.id === id)?.name ?? `Site ${id}`;

  // Any edit to the inputs makes an earlier preview stale.
  const invalidate = () => {
    setPreview(null);
    setError("");
  };

  const send = (dryRun: boolean) => {
    setLoading(true);
    setError("");
    postBulkAlertSettings({
      site_ids: includedIds,
      integration,
      changes,
      filter: cleanFilter(filter),
      dry_run: dryRun,
    }).then(
      (data) => {
        setLoading(false);
        if (dryRun) {
          setPreview(data);
          return;
        }
        data.sites.forEach((site) => {
          if (site.changed_count > 0) markSiteUnsynced(String(site.site_id));
        });
        setResult(data);
      },
      (reason: AxiosError) => {
        setLoading(false);
        const message = bulkErrorMessage(reason);
        if (message) {
          setError(message);
          return;
        }
        universalAPIErrorHandler(
          reason,
          navigate,
          () => send(dryRun),
          () =>
            setError(
              reason.response?.status === 403
                ? NO_PERMISSION_TEXT
                : "Something went wrong saving alert settings. Please try again."
            )
        );
      }
    );
  };

  const deliveryText = (values: PatriotBulkChanges) =>
    patriotDeliveryDisplayText(
      values.patriot_send_video,
      values.draw_ignore_zones,
      ignoreZonesOnAlertPage
    );

  const renderSkipped = (data: BulkAlertSettingsResponse) =>
    data.skipped_sites.length > 0 && (
      <div className="mt-3" data-testid="bulk-skipped-sites">
        <p className="font-mulish font-bold text-sm">
          Skipped {plural(data.skipped_sites.length, "site")}
        </p>
        <ul className="list-disc pl-5 text-sm">
          {data.skipped_sites.map((skipped) => (
            <li key={skipped.site_id}>
              {siteName(skipped.site_id)}: {skipReasonText(skipped.reason)}
            </li>
          ))}
        </ul>
      </div>
    );

  const renderPreview = (data: BulkAlertSettingsResponse) => {
    const { changed, unchanged } = countAlerts(data.sites);
    return (
      <div className="mt-4" data-testid="bulk-preview">
        <p className="font-mulish font-bold">
          {plural(changed, "alert")} will change across{" "}
          {plural(
            data.sites.filter((site) => site.changed_count > 0).length,
            "site"
          )}
          {unchanged > 0 && `; ${unchanged} already set this way`}
        </p>
        <div className="max-h-[320px] overflow-y-auto mt-2 border border-actuate-grey rounded">
          {data.sites.map((site) => (
            <div
              key={site.site_id}
              className="p-2 border-b border-actuate-grey"
            >
              <p className="font-mulish font-bold text-sm">
                {site.site_name} ({site.changed_count} changing)
              </p>
              {site.cameras.map((camera) => (
                <div key={camera.camera_id} className="pl-3 mt-1">
                  <p className="font-mulish text-sm">{camera.camera_name}</p>
                  <ul className="pl-3 text-xs">
                    {camera.alerts.map((alert) => (
                      <li
                        key={alert.alert_id}
                        className={alert.changed ? "" : "text-gray-500"}
                      >
                        Client {alert.patriot_client_no || "-"}, zone{" "}
                        {alert.patriot_zone_number ?? "-"}:{" "}
                        {alert.changed
                          ? `${deliveryText(alert.before)} → ${deliveryText(
                              alert.after
                            )}`
                          : `${deliveryText(alert.before)} (no change)`}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ))}
        </div>
        {renderSkipped(data)}
      </div>
    );
  };

  const renderResult = (data: BulkAlertSettingsResponse) => {
    const changedSites = data.sites.filter((site) => site.changed_count > 0);
    return (
      <div data-testid="bulk-result">
        <p className="font-mulish font-bold">
          Updated {plural(data.changed_count, "alert")} across{" "}
          {plural(changedSites.length, "site")}.
        </p>
        {changedSites.length > 0 && (
          <>
            <p className="font-mulish text-sm mt-2">
              These changes reach each site&apos;s connector after you Sync it.
              Open a site and press Sync, or select the sites here and use Bulk
              Action → SYNC.
            </p>
            <ul className="list-disc pl-5 text-sm mt-1">
              {changedSites.map((site) => (
                <li key={site.site_id}>
                  <a
                    className="text-actuate-blue underline"
                    href={`/sites/aboutsite/${site.site_id}`}
                    onClick={(event) => {
                      event.preventDefault();
                      onClose();
                      navigate(`/sites/aboutsite/${site.site_id}`);
                    }}
                  >
                    {site.site_name}
                  </a>
                </li>
              ))}
            </ul>
          </>
        )}
        {renderSkipped(data)}
      </div>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontFamily: "Mulish" }}>
        Bulk alert settings
      </DialogTitle>
      <DialogContent>
        {result ? (
          renderResult(result)
        ) : (
          <div className="font-mulish">
            <p className="font-bold">
              {allSitesInGroup
                ? `All ${sites.length} sites in ${groupName}`
                : `${plural(sites.length, "selected site")}`}
              {includedIds.length !== sites.length &&
                ` (${includedIds.length} included)`}
            </p>
            <div className="max-h-[160px] overflow-y-auto mt-1 mb-3 flex flex-col">
              {sites.map((site) => (
                <FormControlLabel
                  key={site.id}
                  label={site.name}
                  control={
                    <Checkbox
                      size="small"
                      checked={includedIds.includes(site.id)}
                      onChange={(event, checked) => {
                        setIncludedIds((previous) =>
                          checked
                            ? [...previous, site.id]
                            : previous.filter((id) => id !== site.id)
                        );
                        invalidate();
                      }}
                    />
                  }
                />
              ))}
            </div>

            <TextField
              select
              size="small"
              label="Integration"
              value={integration}
              onChange={(event) => {
                const next = event.target.value as BulkIntegration;
                setIntegration(next);
                invalidate();
              }}
              sx={{ minWidth: "14rem", marginBottom: "1rem" }}
            >
              {BULK_ALERT_INTEGRATIONS.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            {integration === "patriot" && (
              <div className="mb-3">
                <PatriotDeliveryChoice
                  name="bulk-patriot-delivery"
                  sendVideo={changes.patriot_send_video}
                  drawIgnoreZones={changes.draw_ignore_zones}
                  onChange={(values) => {
                    setChanges(values);
                    invalidate();
                  }}
                />
              </div>
            )}

            <p className="font-bold text-sm">
              Only change alerts for (optional)
            </p>
            <div className="flex flex-wrap gap-3 mt-2">
              {integrationConfig.filterFields.map((field) => (
                <TextField
                  key={field.key}
                  size="small"
                  label={field.label}
                  value={filter[field.key] ?? ""}
                  onChange={(event) => {
                    // Capture eagerly; see CLAUDE.md "event.target.value" gotcha.
                    const nextValue = event.target.value;
                    setFilter((previous) => ({
                      ...previous,
                      [field.key]: nextValue,
                    }));
                    invalidate();
                  }}
                />
              ))}
            </div>

            {preview && renderPreview(preview)}
          </div>
        )}
        {error && (
          <p role="alert" className="text-red-600 font-mulish text-sm mt-3">
            {error}
          </p>
        )}
      </DialogContent>
      <DialogActions>
        {loading && <CircularProgress size={20} sx={{ marginRight: "8px" }} />}
        <OrangeTextButton
          onClick={onClose}
          text={result ? "Close" : "Cancel"}
          disabled={false}
        />
        {!result && !preview && (
          <OrangeTextButton
            onClick={() => send(true)}
            text="Preview changes"
            disabled={loading || includedIds.length === 0}
          />
        )}
        {!result && preview && (
          <OrangeTextButton
            onClick={() => send(false)}
            text="Apply"
            disabled={loading || preview.changed_count === 0}
          />
        )}
      </DialogActions>
    </Dialog>
  );
};
